import { View, Text, StyleSheet } from "@react-pdf/renderer";
import type { PdfWidgetSnapshot } from "../pdfTypes";
import type { PdfRect } from "../pdfLayout";

const s = StyleSheet.create({
  cell: {
    position: "absolute",
    borderWidth: 1,
    borderColor: "#e8e8e8",
    borderRadius: 4,
    backgroundColor: "#fff",
    overflow: "hidden",
  },
  title: {
    fontWeight: 600,
    color: "#1a1a1a",
  },
  entry: {
    backgroundColor: "#fafafa",
    borderWidth: 1,
    borderColor: "#f0f0f0",
    flexDirection: "column",
    flexShrink: 0,
  },
  entryHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  entryHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
  },
  playerBadgeView: {
    backgroundColor: "#e6f4ff",
    borderWidth: 1,
    borderColor: "#91caff",
  },
  playerBadgeText: {
    fontWeight: 600,
    color: "#595959",
  },
  dateTag: {
    color: "#8c8c8c",
  },
  authorText: {
    color: "#8c8c8c",
  },
  noteText: {
    color: "#434343",
    lineHeight: 1.55,
  },
  emptyState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    fontSize: 8,
    color: "#8c8c8c",
  },
});

export function renderPlayerNotes(
  snapshot: Extract<PdfWidgetSnapshot, { type: "player_notes" }>,
  rect: PdfRect,
  scale: number,
) {
  // Every measurement is rendered_px × scale, with no inflating floors — the PDF
  // widget rect is itself a pure linear scale of the browser widget (see
  // computePdfRects), so the only way to mirror it exactly is to scale the
  // internal layout by the same factor. Small floors guard extreme zoom-out
  // only; they don't trigger at the normal scale (~0.6).
  const titleTop = 8 * scale;        // .widget-header padding-top
  const contentTop = 37 * scale;     // header height (~33) + .player-notes-content padding-top (4)
  const sidePad = 12 * scale;        // horizontal padding
  const bottomPad = 12 * scale;      // .player-notes-content padding-bottom

  const titleSize = 10;              // matches other widgets' PDF title (≈ 18px × scale)
  const badgeSize = Math.max(5, 11 * scale);
  const authorSize = Math.max(5, 10 * scale);
  const noteSize = Math.max(5, 12 * scale);
  const entryGap = Math.max(2, 6 * scale);     // gap between entries
  const entryPadV = Math.max(4, 10 * scale);   // entry vertical padding
  const entryPadH = Math.max(4, 12 * scale);   // entry horizontal padding
  const headerGap = Math.max(2, 6 * scale);    // entry header → note text
  const badgePadV = Math.max(0.5, 1 * scale);
  const badgePadH = Math.max(3, 7 * scale);
  const badgeMarginR = Math.max(2, 5 * scale);
  const entryRadius = Math.max(2, 6 * scale);
  const badgeRadius = Math.max(1, 4 * scale);

  // Render ALL entries, then let overflow:hidden clip whatever doesn't fit —
  // exactly mirroring the rendered widget's scrolled-to-top view. react-pdf only
  // clips overflow against an absolutely-positioned child (content in normal flow
  // paints past the box), so the entries live in a top-anchored absolute View
  // inside the clip box. Same technique as textPdfRenderer.
  return (
    <View style={[s.cell, { left: rect.left, top: rect.top, width: rect.width, height: rect.height }]}>
      {snapshot.label && (
        <Text style={[s.title, { fontSize: titleSize, position: "absolute", top: titleTop, left: sidePad, right: sidePad }]}>
          {snapshot.label}
        </Text>
      )}
      <View
        style={{
          position: "absolute",
          top: contentTop,
          left: sidePad,
          right: sidePad,
          bottom: bottomPad,
          overflow: "hidden",
        }}
      >
        {snapshot.entries.length === 0 ? (
          <View style={s.emptyState}>
            <Text style={s.emptyText}>No notes found</Text>
          </View>
        ) : (
          <View style={{ position: "absolute", top: 0, left: 0, right: 0 }}>
            {snapshot.entries.map((entry, i) => (
              <View
                key={i}
                style={[
                  s.entry,
                  {
                    paddingVertical: entryPadV,
                    paddingHorizontal: entryPadH,
                    borderRadius: entryRadius,
                    marginBottom: i < snapshot.entries.length - 1 ? entryGap : 0,
                  },
                ]}
              >
                <View style={[s.entryHeader, { marginBottom: headerGap }]}>
                  <View style={s.entryHeaderLeft}>
                    {entry.playerName && (
                      <View
                        style={[
                          s.playerBadgeView,
                          {
                            borderRadius: badgeRadius,
                            marginRight: badgeMarginR,
                            paddingVertical: badgePadV,
                            paddingHorizontal: badgePadH,
                          },
                        ]}
                      >
                        <Text style={[s.playerBadgeText, { fontSize: badgeSize }]}>{entry.playerName}</Text>
                      </View>
                    )}
                    {entry.date && (
                      <Text style={[s.dateTag, { fontSize: badgeSize }]}>{entry.date}</Text>
                    )}
                  </View>
                  {entry.authorName && (
                    <Text style={[s.authorText, { fontSize: authorSize }]}>{entry.authorName}</Text>
                  )}
                </View>
                <Text style={[s.noteText, { fontSize: noteSize }]}>{entry.text}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    </View>
  );
}
